<template>
  <div id="ecommerce-checkout" class="border border-solid d-theme-border-grey-light rounded relative overflow-hidden body-module-height z-20">
    <a :href="urlPayment" ref="paymentURL" v-show="false"></a>

    <!-- UPGRADE CASE -->
    <case-upgrade />
    <!-- UPGRADE CASE -->

    <div class="w-full h-8 px-4 mt-1">
      <vs-button type="border" color="primary" icon-pack="feather" icon="icon-arrow-left-circle" size="small"
                 @click="$router.go(-1)"
                 class="flex justify-center md:text-xs lg:text-base h-8">
        {{ $t('buttons.back') }}
      </vs-button>
    </div>

    <form-wizard
        ref="checkoutWizard"
        color="rgba(var(--vs-primary), 1)"
        :title="null"
        :subtitle="null"
        :hide-buttons="true" class="h-full h-12"
        step-size="xs">

      <!-- tab 1 content -->
      <tab-content :title="$t('pages.shop.checkout.progressBarCart')" icon="feather icon-shopping-cart">
        <bds-scroll-area component-class="content-scroll-area specific-height" v-if="cartProducts.length">
          <div class="h-full flex flex-col md:flex-row">
            <!-- LEFT COL -->
            <div class="h-full w-full md:w-1/3 mb-4">
              <vx-card class="mb-4">
                <h3 class="font-semibold text-primary text-xl mb-3">
                  {{ $t('pages.shop.checkout.tab1.resume') }}
                </h3>

                <template v-if="$store.state.Profile.isClinic()">
                  <p class="font-semibold mb-1">{{ $t('pages.shop.checkout.tab1.chooseDoctor') }}</p>
                  <div class="flex justify-between">
                    <div class="w-full flex bg-white">
                      <choose-user v-model="bind2Doctor" invite-by-email/>
                    </div>
                  </div>
                </template>

                <vs-divider />

                <!-- Coupom -->
                <checkout-coupom />

                <vs-divider/>

                <checkout-service-price-box />

                <vs-divider/>

                <div class="flex justify-between font-semibold mb-3">
                  <span class="font-semibold text-primary text-xl">{{ $t('pages.shop.checkout.tab1.total') }}</span>
                  <span class="font-semibold text-primary text-xl">
                  <template v-if="$store.state.Profile.isBRL()">
                    {{ $store.getters['Shop/getTotalServiceCartPrice'] | BRL }}
                  </template>
                  <template v-else>
                    {{ $store.getters['Shop/getTotalServiceCartPrice'] | USD }}
                  </template>
                </span>
                </div>

                <vs-button class="w-full" @click="$refs.checkoutWizard.nextTab()" :disabled="disablePlaceOrderButton">
                  {{ $t('pages.shop.checkout.tab1.placeOrder') | uppercase }}
                </vs-button>
              </vx-card>
            </div>

            <!-- RIGHT COL -->
            <div class="w-full md:w-2/3 pl-2 h-full">
              <checkout-list-products />
            </div>
          </div>
        </bds-scroll-area>

        <!-- IF NO ITEMS IN CART -->
        <vx-card class="h-48" :title="$t('pages.shop.checkout.tab1.noItemOnCart')" v-else>
          <vs-button @click="$router.push({name: 'shop'})">{{ $t('pages.shop.checkout.tab1.browseShop') | uppercase }}</vs-button>
        </vx-card>
      </tab-content>

      <!-- tab 2 content -->
      <tab-content :title="$t('pages.shop.checkout.progressBarAddress')" icon="feather icon-truck" v-if="hasShipping">
        <bds-scroll-area component-class="content-scroll-area specific-height" v-if="cartProducts.length">
          <address-select-wizard @next-tab="nextTab"/>
        </bds-scroll-area>
      </tab-content>

      <!-- tab 3 content -->
      <tab-content :title="$t('pages.shop.checkout.progressBarPayment')" icon="feather icon-dollar-sign">
        <bds-scroll-area component-class="content-scroll-area specific-height" v-if="cartProducts.length">
          <div class="vx-row">
            <!-- RIGHT COL: PRICE -->
            <div class="vx-col lg:w-1/3 w-full pb-2">
              <vx-card :title="$t('pages.shop.checkout.tab3.priceDetails')">
                <div class="flex justify-between mb-2">
                  <span>{{ $t('pages.shop.checkout.tab3.totalServices') }}</span>
                  <span class="font-semibold" v-if="$store.state.Profile.isBRL()">{{ $store.getters['Shop/getTotalServiceCartPrice'] | BRL }}</span>
                  <span class="font-semibold" v-else>{{ $store.getters['Shop/getTotalServiceCartPrice'] | USD }}</span>
                </div>
                <div class="flex justify-between mb-2" v-if="$store.state.Profile.isBRL()">
                  <span>{{ $t('pages.shop.checkout.tab3.deliveryOfCharge') }}</span>
                  <span class="text-success">{{ totalShippingPrice | BRL }}</span>
                </div>

                <vs-divider/>

                <div class="flex justify-between">
                  <span>{{ $t('pages.shop.checkout.tab3.amountPayable') }}</span>
                  <span class="font-semibold" v-if="$store.state.Profile.isBRL()">{{ totalCartPrice | BRL }}</span>
                  <span class="font-semibold" v-else>{{ totalCartPrice | USD }}</span>
                </div>
              </vx-card>

              <vx-card class="mt-2">
                <div class="w-full flex flex-col">
                  <div class="w-full flex justify-between">
                    <h2>{{ $t('pages.cases.caseView.tabTransaction.invoice.invoiceTitle') }}</h2>
                    <vx-tooltip :text="$t('pages.cases.caseView.tabTransaction.invoice.checkoutInvoiceSetData')" position="top" delay=".3s">
                      <feather-icon icon="UserCheckIcon" class="cursor-pointer mx-1" svg-classes="h-7 w-7 cursor-pointer hover:bg-primary p-1 hover:text-white rounded"
                                    @click="setCustomerInvoiceData"/>
                    </vx-tooltip>
                  </div>

                  <vs-divider class="my-1 p-0"/>

                  <div class="w-full flex flex-row items-center">
                    <span>{{ $t('pages.cases.caseView.tabTransaction.invoice.customerName') }}: </span>
                    <span v-if="invoiceData">{{ invoiceData.customerName }}</span>
                  </div>

                  <div class="w-full flex flex-row items-center">
                    <span>{{ $t('pages.cases.caseView.tabTransaction.invoice.document') }}: </span>
                    <span v-if="invoiceData">{{ invoiceData.document }}</span>
                  </div>

                  <div class="w-full flex flex-row items-center">
                    <span>{{ $t('pages.cases.caseView.tabTransaction.invoice.email') }}: </span>
                    <span v-if="invoiceData">{{ invoiceData.email }}</span>
                  </div>
                </div>
              </vx-card>
            </div>

            <!-- LEFT COL: PAYMENT OPTIONS -->
            <validation-observer v-slot="{ failed }" slim>
              <div class="vx-col lg:w-2/3 w-full">
                <vx-card :title="$t('pages.shop.checkout.tab3.title')" :subtitle="$t('pages.shop.checkout.tab3.subtitle')" class="mb-base">
                  <validation-provider name="paymentMethod" rules="required" immediate v-slot="{ errors }" slim>
                    <div class="mt-3">
                      <payment-choose :currency="$store.state.Profile.getCurrency()" v-model="paymentMethod"/>
                      <bds-error :text_error="errors[0]"/>
                      <vs-divider />
                      <validation-provider name="termsImplant" rules="terms" v-slot="{ errors, valid }" immediate slim>
                        <div class="w-full md:w-1/2">
                          <vs-button type="border" color="primary" size="small" @click="acceptTermsOfService">
                            {{ $t('buttons.readTermsOfService') }}
                          </vs-button>

                          <vs-checkbox v-model="termsOfService">
                            <bds-label :text="$t('pages.shop.checkout.tab3.termsOfService')" :mandatory="!valid"/>
                          </vs-checkbox>
                        </div>
                      </validation-provider>
                    </div>
                  </validation-provider>

                  <vs-button class="mt-6 ml-auto flex" :disabled="failed" @click="placeOrder">
                    {{ $t('pages.shop.checkout.tab3.placeOrder') }}
                  </vs-button>
                </vx-card>
              </div>
            </validation-observer>
          </div>
        </bds-scroll-area>
      </tab-content>
    </form-wizard>
  </div>
</template>

<script>
import {FormWizard, TabContent} from 'vue-form-wizard';
import 'vue-form-wizard/dist/vue-form-wizard.min.css';

import { ValidationProvider, ValidationObserver } from "vee-validate";

import ShopService from '@/services/Shop.service';

import { showErrorNotify } from "@/helpers/functionsHelpers";

import ChooseUser from "@/views/components/ChooseUser";

import CaseUpgrade from "@/views/ecommerce/components/CaseUpgrade";
import PaymentChoose from "@/views/ecommerce/components/PaymentChoose";
import {CaseInvoice} from "@/models/cases/CaseInvoice.model";

import CheckoutCoupom from "@/views/ecommerce/components/CheckoutCoupom";
import CheckoutServicePriceBox from '@/views/ecommerce/components/CheckoutServicePriceBox';
import CheckoutListProducts from "@/views/ecommerce/components/CheckoutListProducts";

import AddressSelectWizard from "@/views/ecommerce/components/AddressSelectWizard";

import {PAYMENT_PG_PHYSICAL, PAYMENT_PIX, PAYMENT_DENTAL_PREV} from '@/models/cases/CaseTransaction.model';
import {Shipping} from "@/models/shipping.model";

export default {
  components: {
    PaymentChoose,
    CaseUpgrade,
    ChooseUser,
    FormWizard,
    TabContent,
    ValidationProvider,
    ValidationObserver,
    CheckoutListProducts,
    CheckoutCoupom,
    CheckoutServicePriceBox,
    AddressSelectWizard
  },
  data() {
    return {
      //TAB 1
      bind2Doctor: 0,

      // TAB 3
      paymentMethod: '',
      termsOfService: false,
      urlPayment: null,
      invoiceData: null,
    }
  },
  watch: {
    bind2Doctor() {
      if (this.bind2Doctor && this.bind2Doctor > 0) {
        this.$store.dispatch('Users/lookupUser', this.bind2Doctor).then((user) => {
          this.doctorAddress = user.dataAddress;
        });
      } else {
        this.doctorAddress = null;
      }
    },
    hasShipping(value) {
      if (!value) {
        this.$store.dispatch('Shop/setShipment', new Shipping());
      }
    }
  },
  computed: {
    isSmallerScreen () {
      return this.$store.state.isSmallerScreen();
    },
    cartProducts() {
      return this.$store.state.Shop.cartItems;
    },
    isInCart() {
      return (itemId) => this.$store.getters['Shop/isInCart'](itemId);
    },
    totalCartPrice() {
      return this.$store.getters['Shop/getTotalOrderPrice'];
    },

    hasShipping() {
      return this.$store.getters['Shop/hasShippingInCart'];
    },
    totalShippingPrice() {
      return this.$store.getters['Shop/getShipmentFee'];
    },

    disablePlaceOrderButton() {
      return !this.$store.getters['Shop/isPlaceOrderValid']();
    }
  },
  methods: {
    nextTab() {
      this.$refs.checkoutWizard.nextTab();
    },
    // TAB 3
    acceptTermsOfService() {
      if (this.termsOfService) {
        return Promise.resolve(this.termsOfService);
      } else {
        return this.$alma.dialogTermsOfUse().then((accepted) => {
          this.termsOfService = accepted;
          return Promise.resolve(accepted);
        });
      }
    },
    setCustomerInvoiceData() {
      this.$alma.dialogInvoice({ invoiceData: this.invoiceData }).then((invoice) => {
        if (!invoice) {
          return;
        }
        this.invoiceData = CaseInvoice.FromObj(invoice);
      });
    },
    placeOrder() {
      this.acceptTermsOfService().then((accepted) => {
        if (!accepted) {
          return;
        }

        let productArray = {};
        for (let p of this.$store.state.Shop.cartItems) {
          productArray[p.id] = p.innerCheckoutProducts;
        }

        const payload = {
          caseId: this.$store.state.Shop.upgradeCase,
          products: productArray,
          cupomId: this.$store.state.Shop.getCoupomId(),
          orderPrice: this.totalCartPrice.toFixed(2),
          paymentMethod: this.paymentMethod,
          doctorId: this.bind2Doctor,
          shipment: this.$store.state.Shop.getShipping(),
          invoice: this.invoiceData,
        }

        this.$vs.loading({
          text: this.$t('pages.shop.checkout.tab3.loadingCheckoutOrder')
        });

        ShopService.checkoutOrder(payload).then((response) => {
          this.$store.dispatch('Shop/resetCart').then(() => {
            if ([PAYMENT_PG_PHYSICAL, PAYMENT_PIX, PAYMENT_DENTAL_PREV].includes(response.paymentMethod)) {
              this.$vs.loading.close();
              let title = `pages.shop.checkout.tab3.${response.paymentMethod}MsgTitle`;
              let body = `pages.shop.checkout.tab3.${response.paymentMethod}MsgBody`;
              this.$alma.dialogHtml({
                isOkModal: true,
                title: this.$t(title),
                html: this.$t(body, { pixCode: response.paymentLink }),
              }).then(() => {
                this.$router.push({name: 'cases-new'});
              });
            } else {
              this.urlPayment = response.paymentLink;
              this.$nextTick(() => {
                this.$refs.paymentURL.click();
              });
            }
          });
        }, error => {
          this.$vs.loading.close();
          showErrorNotify(this, error);
        });
      });
    }
  },
  created() {

  },
  mounted() {

  }
}
</script>

<style lang="scss" scoped>
  #ecommerce-checkout {
    .item-view-primary-action-btn {
      color: #2c2c2c !important;
      background-color: #f6f6f6;
    }

    .item-name {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      overflow: hidden;
      -webkit-line-clamp: 2;
    }

    .vue-form-wizard {
      padding-bottom: 0;

      ::v-deep .wizard-tab-content {
        padding-top: 10px;
      }

      ::v-deep .wizard-header {
        padding: 0;
      }

      ::v-deep .wizard-navigation {
        height: 100%;
      }

      ::v-deep .wizard-tab-content {
        padding-right: 0;
        padding-left: 0;
        padding-bottom: 0;
        height: 100%;

        .wizard-tab-container {
          margin-bottom: 0 !important;
          height: 100%;
        }
      }
    }

    .content-scroll-area.specific-height {
      height: calc(100% - 68px - 2rem - 0.25rem - 1rem);
      margin: 0 !important;
    }

    @media screen and (min-width: $screen-md-min) {
      .content-scroll-area.specific-height {
        height: calc(100% - 68px - 2rem - 0.25rem - 1rem);
      }
    }
  }

  span.vs-circle-switch.vs-switch--circle {
    background: var(--vs-success) !important;
    border-width: 1px !important;
    border-color: rgba(113, 128, 150, var(--border-opacity)) !important;
  }

  ::v-deep .vs-checkbox--input {
    z-index: 10 !important;
  }

</style>
